export function sanitizeHtml(dirtyHtml) {
  if (!dirtyHtml) return '';

  if (typeof document === 'undefined') return '';

  const container = document.createElement('div');
  container.innerHTML = String(dirtyHtml);

  const allowedTags = new Set([
    'a',
    'b',
    'strong',
    'i',
    'em',
    'u',
    'p',
    'br',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    'hr',
    'span',
    'div',
    'img',
  ]);

  const allowedAttrsByTag = {
    a: new Set(['href', 'target', 'rel']),
    img: new Set(['src', 'alt', 'title', 'width', 'height', 'style']),
    '*': new Set(['style']),
  };

  const isSafeUrl = (url) => {
    if (!url) return false;
    const trimmed = String(url).trim();
    if (!trimmed) return false;

    if (trimmed.startsWith('#')) return true;

    try {
      const u = new URL(trimmed, window.location.origin);
      return ['http:', 'https:', 'mailto:'].includes(u.protocol);
    } catch {
      return false;
    }
  };

  const sanitizeNode = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();

      if (!allowedTags.has(tag)) {
        const parent = node.parentNode;
        if (!parent) return;

        while (node.firstChild) parent.insertBefore(node.firstChild, node);
        parent.removeChild(node);
        return;
      }

      const allowedAttrs = new Set([
        ...(allowedAttrsByTag['*'] ? Array.from(allowedAttrsByTag['*']) : []),
        ...(allowedAttrsByTag[tag] ? Array.from(allowedAttrsByTag[tag]) : []),
      ]);

      for (const attr of Array.from(node.attributes)) {
        const name = attr.name.toLowerCase();
        const value = attr.value;

        if (name.startsWith('on')) {
          node.removeAttribute(attr.name);
          continue;
        }

        if (!allowedAttrs.has(name)) {
          node.removeAttribute(attr.name);
          continue;
        }

        if (tag === 'a' && name === 'href' && !isSafeUrl(value)) {
          node.removeAttribute(attr.name);
        }

        if (tag === 'img' && name === 'src' && !isSafeUrl(value)) {
          node.removeAttribute(attr.name);
        }

        if (name === 'style') {
          const safeStyle = String(value)
            .split(';')
            .map((s) => s.trim())
            .filter(Boolean)
            .filter((decl) => {
              const [prop] = decl.split(':').map((x) => (x || '').trim().toLowerCase());
              return ['text-align', 'font-weight', 'font-style', 'text-decoration', 'color', 'background-color', 'max-width', 'height', 'width', 'border-radius'].includes(prop);
            })
            .join('; ');

          if (safeStyle) node.setAttribute('style', safeStyle);
          else node.removeAttribute('style');
        }
      }

      if (tag === 'a') {
        if (!node.getAttribute('target')) node.setAttribute('target', '_blank');
        const rel = node.getAttribute('rel') || '';
        const relTokens = new Set(rel.split(' ').map((t) => t.trim()).filter(Boolean));
        relTokens.add('noopener');
        relTokens.add('noreferrer');
        node.setAttribute('rel', Array.from(relTokens).join(' '));
      }

      if (tag === 'img') {
        const style = node.getAttribute('style') || '';
        if (!/max-width\s*:\s*100%/i.test(style)) {
          node.setAttribute(
            'style',
            `${style ? `${style}; ` : ''}max-width: 100%; height: auto; border-radius: 12px;`
          );
        }
      }
    }

    for (const child of Array.from(node.childNodes)) {
      sanitizeNode(child);
    }
  };

  sanitizeNode(container);
  return container.innerHTML;
}

export function isHtmlLike(value) {
  if (value == null) return false;
  const s = String(value);
  if (!s.trim()) return false;
  return /<\s*\/?\s*[a-z][^>]*>/i.test(s) || /&[a-z]+;|&#\d+;|&#x[0-9a-f]+;/i.test(s);
}
